/**
 * Routes an organization's login landing to that organization's own subdomain.
 *
 * Auth0 builds an organization invitation url from the Application Login URI of
 * the application it was minted against, and that field holds exactly one host.
 * A client served at their own subdomain therefore either has a dedicated Auth0
 * application pointing straight at it -- in which case the invitation already
 * arrives there and nothing here fires -- or shares an application with everyone
 * else, lands on the shared host, and is redirected the rest of the way here.
 *
 * REACT_APP_ORG_SUBDOMAIN_MAP is the list of organizations in the second group. An
 * organization absent from it stays on the host it landed on, which is correct:
 * organization identity travels in the `organization` parameter, not the
 * hostname. That absence is also what keeps ordinary organizations off hosts
 * that were never added to the application's Allowed Callback URLs, where
 * /authorize would fail on a callback mismatch.
 */

// Entries are `slug:subdomain`, or bare `slug` where the two match.
const parseSubdomains = (raw) => {
  const map = new Map();
  (raw || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .forEach((entry) => {
      const [slug, subdomain] = entry.split(':').map((part) => part.trim());
      if (slug) map.set(slug, subdomain || slug);
    });
  return map;
};

/**
 * The domain organization subdomains hang off — `ledgible.io`, so that the `citi`
 * entry above resolves to `citi.ledgible.io`.
 *
 * Derived from REACT_APP_AUTH0_CALLBACK rather than configured, because every
 * REACT_APP_ variable costs an ARG in docker/Dockerfile, an export and a
 * --build-arg in each CircleCI build job, and a project variable per
 * environment. The shared deployment is the only one that ever redirects, and
 * its callback host is by construction one label below the root the subdomains
 * hang off: app.ledgible.io -> ledgible.io, app-uat.ledgible.io -> ledgible.io.
 *
 * REACT_APP_APP_DOMAIN overrides it. **Nothing sets that variable today, and it
 * is not plumbed through Docker or CircleCI.** It exists for the day the app's
 * hostname stops being exactly one label above the subdomain root — the app
 * moving to the apex, an extra label appearing in the app host while vanity
 * hosts stay put, or vanity hosts moving to a different domain than the app.
 * All three are deliberate infrastructure changes, so plumbing the variable
 * then is a small part of a much larger piece of work. Do not set it otherwise.
 *
 * The two-label floor is what makes the apex case safe: an app at
 * `https://ledgible.io` would otherwise derive `io` and send every mapped
 * organization to a domain we do not own. Below two labels this returns
 * nothing, which turns the feature off rather than pointing it somewhere else.
 */
export const getAppDomain = () => {
  const override = import.meta.env?.REACT_APP_APP_DOMAIN;
  if (override) return override;

  const callback = import.meta.env?.REACT_APP_AUTH0_CALLBACK;
  if (!callback) return undefined;

  let hostname;
  try {
    ({ hostname } = new URL(callback));
  } catch (e) {
    return undefined;
  }

  const parent = hostname.split('.').slice(1).join('.');
  return parent.includes('.') ? parent : undefined;
};

// The resolved subdomain must be a single hostname label, so interpolation
// cannot escape the configured domain. Organization slugs are not checked
// against this: Auth0 permits underscores in an organization `name`, which is
// never a valid label but is a perfectly good key into the map above. What ends
// up in the URL is the mapped value, and that is what has to be safe.
const LABEL = /^[a-z0-9-]+$/;

/**
 * The host this login should be served from, or null to stay put. Null whenever
 * the domain is unconfigured, the organization has no subdomain, the query
 * carries no organization_name, or we are already there.
 */
export const resolveOrgSubdomainHost = ({ location, domain, subdomains } = {}) => {
  const loc = location || (typeof window !== 'undefined' ? window.location : null);
  const appDomain = domain ?? getAppDomain();

  if (!loc || !appDomain) return null;

  const orgName = new URLSearchParams(loc.search || '').get('organization_name');
  if (!orgName) return null;

  const configured =
    subdomains ?? parseSubdomains(import.meta.env?.REACT_APP_ORG_SUBDOMAIN_MAP);
  const subdomain = configured.get(orgName);
  if (!subdomain || !LABEL.test(subdomain)) return null;

  const host = `${subdomain}.${appDomain}`;
  return host === loc.host ? null : host;
};

/**
 * True when the current host is one of the configured organization subdomains.
 *
 * The provider default `redirect_uri` in `getAuth0DefaultClientOptions` is built
 * from REACT_APP_AUTH0_CALLBACK, a fixed host. Left alone it would return a user
 * who logged in on their own subdomain to the shared host, undoing the redirect
 * at the callback. Logins on an organization subdomain therefore pass a
 * host-aware `redirect_uri` instead -- and only those, so ordinary logins keep
 * using the configured callback exactly as before.
 */
export const isOrgSubdomainHost = ({ location, domain, subdomains } = {}) => {
  const loc = location || (typeof window !== 'undefined' ? window.location : null);
  const appDomain = domain ?? getAppDomain();

  if (!loc || !appDomain) return false;

  const configured =
    subdomains ?? parseSubdomains(import.meta.env?.REACT_APP_ORG_SUBDOMAIN_MAP);

  return [...configured.values()].some(
    (subdomain) => `${subdomain}.${appDomain}` === loc.host,
  );
};

/**
 * The origin to build absolute app URLs from — logout `returnTo`, the guard
 * bounces, error recovery.
 *
 * REACT_APP_AUTH0_CALLBACK is a single fixed host, so on an organization
 * subdomain every one of those would move the user back to the shared host
 * mid-session. This returns the current origin there, and the configured
 * callback everywhere else, so nothing changes for a deployment that has no
 * subdomains configured.
 *
 * Every origin this can return must be in the application's Allowed Logout URLs,
 * which is already part of per-client onboarding.
 */
export const getAppOrigin = (options = {}) => {
  const loc = options.location || (typeof window !== 'undefined' ? window.location : null);
  if (loc && isOrgSubdomainHost(options)) return loc.origin;
  return import.meta.env?.REACT_APP_AUTH0_CALLBACK;
};

/**
 * The `redirect_uri` a login started on this host should use, or null to leave
 * the provider default alone.
 */
export const getOrgSubdomainRedirectUri = (options = {}) => {
  const loc = options.location || (typeof window !== 'undefined' ? window.location : null);
  if (!loc || !isOrgSubdomainHost(options)) return null;
  return `${loc.protocol}//${loc.host}/auth-callback`;
};

/**
 * Redirect to the organization's subdomain if it belongs on one. Returns true
 * when a redirect was started, in which case the caller must not go on to start
 * a login -- the two would race.
 *
 * The query string is forwarded whole rather than rebuilt: `invitation` is the
 * ticket that accepts organization membership, and losing it leaves the user
 * authenticated with no org_id claim, on the invitation-required page.
 */
export const redirectToOrgSubdomain = (options = {}) => {
  const loc = options.location || (typeof window !== 'undefined' ? window.location : null);
  const host = resolveOrgSubdomainHost(options);

  if (!host || !loc) return false;

  loc.replace(`https://${host}${loc.pathname}${loc.search}`);
  return true;
};
